/**
 * One article's web fiche — the design's three-column panel.
 *
 * Left: what the ERP holds, read-only. Middle: what the back office adds for
 * the web. Right: the completion report and the publish button. The columns
 * are that way round on purpose — the operator reads the ERP's version, writes
 * the customer's, and then decides — and the third column stays visible while
 * they type, because "what is still missing" is the question they are
 * answering.
 *
 * **A photo upload is two requests, and the first one is not ours.** The bytes
 * go to the `files` module, which owns storage, the size limit and the type
 * allowlist; the catalogue then records what they are for. Doing it in one
 * request would mean a second upload path with its own limits, and one of the
 * two would eventually stop being maintained.
 *
 * **The inherited category is labelled as inherited.** An article with no
 * category of its own shows its family's, and an operator who cannot tell the
 * difference will "fix" a category that was never set — then wonder why every
 * other article of the family changed too.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { money } from "@hardauto/core-i18n";
import {
  Alert,
  Badge,
  Button,
  Card,
  DefinitionList,
  ErrorState,
  Input,
  LoadingState,
  Meter,
  Page,
  Select,
  StatusDot,
  Textarea,
  useToast,
} from "@hardauto/core-ui";

import { CatalogApi, type Fiche, type Media, type WebCategory } from "../api";
import { completionTone, criterionLabelKey } from "../criteria";
import { NAMESPACE } from "../i18n";
import { CATALOG_PATHS } from "../paths";
import "./catalog.css";

/** What the API caps a gallery at. Mirrors the domain's `MAX_PHOTOS`. */
const MAX_PHOTOS = 6;

export function FicheScreen({ client, id }: { client: ApiClient; id: string }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new CatalogApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("catalog.enrichment.write");
  const mayPublish = useCan("catalog.publish");

  const fetcher = useCallback(() => api.fiche(id), [api, id]);
  const fiche = useResource(fetcher);

  const categoriesFetcher = useCallback(() => api.categoryList(false), [api]);
  const categories = useResource(categoriesFetcher);

  const [draft, setDraft] = useState<{
    webDesignation: string;
    description: string;
    webBrand: string;
    categoryId: string;
    keywords: string;
  } | null>(null);

  // Reset the form whenever a different fiche arrives. Without the guard on
  // the article id, a save that returns the same fiche would wipe an edit the
  // operator made while the request was in flight.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    const data = fiche.data;
    if (!data || loadedFor.current === data.facts.id) return;
    loadedFor.current = data.facts.id;
    setDraft({
      webDesignation: data.web_designation,
      description: data.description,
      webBrand: data.web_brand,
      // The inherited category is deliberately NOT preselected: choosing it
      // explicitly is a different decision from letting the family decide, and
      // the select's empty option says so.
      categoryId: data.category_inherited ? "" : (data.category_id ?? ""),
      keywords: data.keywords.join("\n"),
    });
  }, [fiche.data]);

  if (fiche.loading && !fiche.data) {
    return (
      <Page title={t("fiche.loading")}>
        <LoadingState label={t("fiche.loading")} />
      </Page>
    );
  }

  if (!fiche.data) {
    return (
      <Page title={t("fiche.notFound.title")}>
        <ErrorState
          title={t("fiche.notFound.title")}
          description={t("fiche.notFound.body")}
          {...(fiche.failed ? { onRetry: fiche.reload, retryLabel: t("error.retry") } : {})}
        />
      </Page>
    );
  }

  const data = fiche.data;
  const form = draft ?? {
    webDesignation: data.web_designation,
    description: data.description,
    webBrand: data.web_brand,
    categoryId: data.category_inherited ? "" : (data.category_id ?? ""),
    keywords: data.keywords.join("\n"),
  };

  function save() {
    void action.run("save", async () => {
      try {
        await api.saveFiche(id, {
          web_designation: form.webDesignation,
          description: form.description,
          web_brand: form.webBrand,
          ...(form.categoryId ? { category_id: form.categoryId } : { clear_category: true }),
          keywords: form.keywords
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean),
        });
        toast.show(t("fiche.web.saved"), { tone: "success" });
        loadedFor.current = null;
        fiche.reload();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      }
    });
  }

  return (
    <Page
      title={data.web_designation || data.facts.designation}
      eyebrow={`${data.facts.reference} · ${data.facts.family_label || data.facts.family_code}`}
      back={<Link to={CATALOG_PATHS.fiches}>{t("fiche.back")}</Link>}
    >
      <div className="hardauto-catalog__columns">
        <ErpColumn data={data} locale={locale} t={t} />

        <Card title={t("fiche.web.title")}>
          <form
            className="hardauto-stack"
            onSubmit={(event) => {
              event.preventDefault();
              save();
            }}
          >
            <Field label={t("fiche.web.designation")} hint={t("fiche.web.designationHint")}>
              {(props) => (
                <Input
                  {...props}
                  value={form.webDesignation}
                  disabled={!mayWrite}
                  onChange={(event) => setDraft({ ...form, webDesignation: event.target.value })}
                />
              )}
            </Field>

            <Field label={t("fiche.web.description")}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={4}
                  value={form.description}
                  disabled={!mayWrite}
                  onChange={(event) => setDraft({ ...form, description: event.target.value })}
                />
              )}
            </Field>

            <Field label={t("fiche.web.brand")}>
              {(props) => (
                <Input
                  {...props}
                  value={form.webBrand}
                  disabled={!mayWrite}
                  onChange={(event) => setDraft({ ...form, webBrand: event.target.value })}
                />
              )}
            </Field>

            <Field
              label={t("fiche.web.category")}
              hint={
                data.category_inherited && data.category_path
                  ? t("fiche.web.categoryInherited", {
                      family: data.facts.family_label || data.facts.family_code,
                    })
                  : undefined
              }
            >
              {(props) => (
                <Select
                  {...props}
                  value={form.categoryId}
                  disabled={!mayWrite}
                  onChange={(event) => setDraft({ ...form, categoryId: event.target.value })}
                >
                  <option value="">{t("fiche.web.inheritedOption")}</option>
                  {(categories.data?.items ?? []).map((category: WebCategory) => (
                    <option key={category.id} value={category.id}>
                      {`${"  ".repeat(Math.max(0, category.depth - 1))}${category.label}`}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t("fiche.web.keywords")} hint={t("fiche.web.keywordsHint")}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  value={form.keywords}
                  disabled={!mayWrite}
                  onChange={(event) => setDraft({ ...form, keywords: event.target.value })}
                />
              )}
            </Field>

            {mayWrite ? (
              <Button
                type="submit"
                variant="primary"
                busy={action.isBusy("save")}
                busyLabel={t("fiche.web.saving")}
              >
                {t("fiche.web.save")}
              </Button>
            ) : null}
          </form>
        </Card>

        <PublicationColumn
          data={data}
          mayPublish={mayPublish}
          busy={action.isBusy("publish")}
          onPublish={() => {
            void action.run("publish", async () => {
              try {
                if (data.facts.status === "published") {
                  await api.unpublish(id);
                  toast.show(t("fiche.completion.unpublished"), { tone: "success" });
                } else {
                  await api.publish(id);
                  toast.show(t("fiche.completion.published"), { tone: "success" });
                }
                loadedFor.current = null;
                fiche.reload();
              } catch {
                toast.show(t("error.title"), { tone: "danger" });
              }
            });
          }}
          t={t}
        />
      </div>

      <div className="hardauto-catalog__split">
        <MediaCard
          api={api}
          articleId={id}
          data={data}
          mayWrite={mayWrite}
          onChanged={() => {
            loadedFor.current = null;
            fiche.reload();
          }}
        />
        <CharacteristicsCard data={data} t={t} />
      </div>
    </Page>
  );
}

type Translate = ReturnType<typeof useTranslation>["t"];

/** The ERP's side. Read-only, because the referential owns these fields. */
function ErpColumn({ data, locale, t }: { data: Fiche; locale: string; t: Translate }) {
  return (
    <Card title={t("fiche.erp.title")}>
      <DefinitionList
        items={[
          { label: t("fiche.erp.reference"), value: <code>{data.facts.reference}</code> },
          { label: t("fiche.erp.designation"), value: data.facts.designation },
          { label: t("fiche.erp.brand"), value: data.facts.brand || "—" },
          {
            label: t("fiche.erp.family"),
            value: data.facts.family_label || data.facts.family_code || "—",
          },
          { label: t("fiche.erp.unit"), value: data.facts.sales_unit },
          { label: t("fiche.erp.price"), value: money(data.facts.base_price, locale) },
          {
            label: t("fiche.erp.status"),
            value: (
              <Badge tone={data.facts.status === "published" ? "success" : "neutral"}>
                {t(`fiches.status.${data.facts.status}`, { defaultValue: data.facts.status })}
              </Badge>
            ),
          },
        ]}
      />
      {data.facts.oe_references.length > 0 ? (
        <div className="hardauto-stack">
          <h3 className="hardauto-eyebrow">{t("fiche.equivalences.title")}</h3>
          <ul className="hardauto-refs">
            {data.facts.oe_references.map((reference) => (
              <li key={reference}>
                <code>{reference}</code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

/** The completion report, and the one button that acts on it. */
function PublicationColumn({
  data,
  mayPublish,
  busy,
  onPublish,
  t,
}: {
  data: Fiche;
  mayPublish: boolean;
  busy: boolean;
  onPublish: () => void;
  t: Translate;
}) {
  const published = data.facts.status === "published";
  const report = data.completion;

  return (
    <Card title={t("fiche.completion.title")}>
      <div className="hardauto-stack">
        <Meter
          value={report.score}
          label={t("fiche.completion.score")}
          tone={completionTone(report.score, report.publishable)}
        />

        {report.publishable ? (
          <Alert tone="success">{t("fiche.completion.publishable")}</Alert>
        ) : (
          <Alert tone="warning" title={t("fiche.completion.blocked")}>
            {t("fiche.completion.blockedBy", {
              criteria: report.blocking
                .map((criterion) => t(criterionLabelKey(criterion), { defaultValue: criterion }))
                .join(", "),
            })}
          </Alert>
        )}

        <ul className="hardauto-criteria">
          {report.criteria.map((criterion) => (
            <li key={criterion.criterion}>
              <StatusDot
                label={t(criterionLabelKey(criterion.criterion), {
                  defaultValue: criterion.criterion,
                })}
                tone={criterion.met ? "success" : criterion.blocking ? "danger" : "neutral"}
              />
              <span className="hardauto-cell__muted">
                {criterion.blocking
                  ? t("fiche.completion.blocking")
                  : t("fiche.completion.optional")}
              </span>
            </li>
          ))}
        </ul>

        {mayPublish ? (
          <Button
            variant={published ? "secondary" : "primary"}
            // Withdrawing is always allowed; publishing is not. The button is
            // disabled rather than hidden so the reader can see the action
            // exists and why it is unavailable — the alert above says which.
            disabled={!published && !report.publishable}
            busy={busy}
            busyLabel={
              published ? t("fiche.completion.unpublishing") : t("fiche.completion.publishing")
            }
            onClick={onPublish}
          >
            {published ? t("fiche.completion.unpublish") : t("fiche.completion.publish")}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

/** Photos and documents. */
function MediaCard({
  api,
  articleId,
  data,
  mayWrite,
  onChanged,
}: {
  api: CatalogApi;
  articleId: string;
  data: Fiche;
  mayWrite: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const photoInput = useRef<HTMLInputElement>(null);
  const documentInput = useRef<HTMLInputElement>(null);

  async function upload(file: File, kind: "photo" | "document") {
    const body = new FormData();
    body.append("upload", file);
    // Two requests, and the first is the files module's: it owns storage, the
    // size limit and the type allowlist. See the module docstring.
    const record = await api.uploadFile(body);
    await api.attach(articleId, { kind, file_id: record.id, label: file.name });
  }

  function pick(kind: "photo" | "document", input: HTMLInputElement | null) {
    const file = input?.files?.[0];
    if (!file) return;
    void action.run(`upload:${kind}`, async () => {
      try {
        await upload(file, kind);
        toast.show(t("fiche.media.added"), { tone: "success" });
        onChanged();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      } finally {
        if (input) input.value = "";
      }
    });
  }

  return (
    <Card
      title={t("fiche.media.photos")}
      description={t("fiche.media.photosCount", {
        count: data.photos.length,
        max: MAX_PHOTOS,
      })}
    >
      <div className="hardauto-stack">
        {data.photos.length === 0 ? (
          <p className="hardauto-cell__muted">{t("fiche.media.emptyPhotos")}</p>
        ) : (
          <ul className="hardauto-media">
            {data.photos.map((item) => (
              <MediaRow
                key={item.id}
                item={item}
                mayWrite={mayWrite}
                busy={action.isBusy(item.id)}
                onPrimary={() => {
                  void action.run(item.id, async () => {
                    await api.setPrimary(articleId, item.id);
                    onChanged();
                  });
                }}
                onRemove={() => {
                  void action.run(item.id, async () => {
                    await api.detach(item.id);
                    toast.show(t("fiche.media.removed"), { tone: "success" });
                    onChanged();
                  });
                }}
              />
            ))}
          </ul>
        )}

        {mayWrite && data.photos.length < MAX_PHOTOS ? (
          <>
            {/* The file input is hidden and driven by the button: a bare
                `<input type="file">` cannot be styled, and the button carries
                the busy state the upload needs. */}
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              className="hardauto-visually-hidden"
              onChange={() => pick("photo", photoInput.current)}
            />
            <Button
              busy={action.isBusy("upload:photo")}
              busyLabel={t("fiche.media.adding")}
              onClick={() => photoInput.current?.click()}
            >
              {t("fiche.media.add")}
            </Button>
          </>
        ) : null}

        <h3 className="hardauto-eyebrow">{t("fiche.media.documents")}</h3>
        {data.documents.length === 0 ? (
          <p className="hardauto-cell__muted">{t("fiche.media.emptyDocuments")}</p>
        ) : (
          <ul className="hardauto-media">
            {data.documents.map((item) => (
              <MediaRow
                key={item.id}
                item={item}
                mayWrite={mayWrite}
                busy={action.isBusy(item.id)}
                onRemove={() => {
                  void action.run(item.id, async () => {
                    await api.detach(item.id);
                    toast.show(t("fiche.media.removed"), { tone: "success" });
                    onChanged();
                  });
                }}
              />
            ))}
          </ul>
        )}

        {mayWrite ? (
          <>
            <input
              ref={documentInput}
              type="file"
              className="hardauto-visually-hidden"
              onChange={() => pick("document", documentInput.current)}
            />
            <Button
              busy={action.isBusy("upload:document")}
              busyLabel={t("fiche.media.adding")}
              onClick={() => documentInput.current?.click()}
            >
              {t("fiche.media.addDocument")}
            </Button>
          </>
        ) : null}
      </div>
    </Card>
  );
}

function MediaRow({
  item,
  mayWrite,
  busy,
  onPrimary,
  onRemove,
}: {
  item: Media;
  mayWrite: boolean;
  busy: boolean;
  onPrimary?: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <li className="hardauto-media__row">
      <span className="hardauto-media__label">{item.label || item.file_id}</span>
      {item.is_primary ? <Badge tone="info">{t("fiche.media.primary")}</Badge> : null}
      {mayWrite ? (
        <span className="hardauto-media__actions">
          {onPrimary && !item.is_primary ? (
            <Button size="sm" busy={busy} busyLabel={t("fiche.media.adding")} onClick={onPrimary}>
              {t("fiche.media.makePrimary")}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="danger"
            busy={busy}
            busyLabel={t("fiche.media.removing")}
            onClick={onRemove}
          >
            {t("fiche.media.remove")}
          </Button>
        </span>
      ) : null}
    </li>
  );
}

/** The characteristics table, with what each value is and where it came from. */
function CharacteristicsCard({ data, t }: { data: Fiche; t: Translate }) {
  const required = new Set(data.facts.required_attributes);
  const present = new Set(data.facts.attributes.map((attribute) => attribute.key));
  const missing = data.facts.required_attributes.filter((key) => !present.has(key));

  return (
    <Card title={t("fiche.characteristics.title")} flush>
      {data.facts.attributes.length === 0 && missing.length === 0 ? (
        <p className="hardauto-cell__muted hardauto-card__pad">
          {t("fiche.characteristics.empty")}
        </p>
      ) : (
        <table className="hardauto-table">
          <caption className="hardauto-visually-hidden">
            {t("fiche.characteristics.caption")}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t("fiche.characteristics.attribute")}</th>
              <th scope="col">{t("fiche.characteristics.value")}</th>
              <th scope="col">{t("fiche.characteristics.source")}</th>
              <th scope="col">{t("fiche.characteristics.filterable")}</th>
            </tr>
          </thead>
          <tbody>
            {data.facts.attributes.map((attribute) => (
              <tr key={attribute.key}>
                <th scope="row">
                  <code>{attribute.key}</code>
                  {required.has(attribute.key) ? (
                    <Badge tone="info">{t("fiche.characteristics.required")}</Badge>
                  ) : null}
                </th>
                <td>{attribute.value}</td>
                <td className="hardauto-cell__muted">
                  {t(`fiche.characteristics.sourceLabel.${attribute.source}`, {
                    defaultValue: attribute.source,
                  })}
                </td>
                <td>{attribute.is_filterable ? "✓" : ""}</td>
              </tr>
            ))}
            {/* The missing required ones, listed as rows rather than mentioned
                in a sentence: the operator is going to fill them in, and a
                table they are already reading is where they will look. */}
            {missing.map((key) => (
              <tr key={key} data-missing="true">
                <th scope="row">
                  <code>{key}</code>
                  <Badge tone="danger">{t("fiche.characteristics.missing")}</Badge>
                </th>
                <td colSpan={3} className="hardauto-cell__muted">
                  —
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
